import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatErr, today, downloadBlob } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Truck, FileDown, ClipboardList, MapPin, Navigation, Map as MapIcon, RefreshCw, Radio, User, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["Pending", "Out For Delivery", "Delivered", "Partially Delivered", "Cancelled", "Unable To Deliver"];
const STATUS_COLOR = {
  "Pending": "bg-slate-100 text-slate-700",
  "Out For Delivery": "bg-amber-50 text-amber-700",
  "Delivered": "bg-emerald-50 text-emerald-700",
  "Partially Delivered": "bg-cobalt-50 text-cobalt-700",
  "Cancelled": "bg-rose-50 text-rose-700",
  "Unable To Deliver": "bg-rose-50 text-rose-700",
};

export default function Deliveries() {
  const [list, setList] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [status, setStatus] = useState("");
  const [driverId, setDriverId] = useState("");
  const [routeId, setRouteId] = useState("");
  const [date, setDate] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [assignDriver, setAssignDriver] = useState("");
  const [assignRoute, setAssignRoute] = useState("");
  const [assignDate, setAssignDate] = useState(today());

  // GPS Map State
  const [mapOpen, setMapOpen] = useState(false);
  const [mapDriverId, setMapDriverId] = useState("all");
  const [activeDriverPin, setActiveDriverPin] = useState(null);

  function load() {
    const params = {};
    if (status) params.status = status;
    if (driverId) params.driver_id = driverId;
    if (routeId) params.route_id = routeId;
    if (date) { params.frm = date; params.to = date; }
    api.get("/deliveries", { params }).then(r => setList(r.data));
    api.get("/drivers").then(r => setDrivers(r.data));
    api.get("/routes").then(r => setRoutes(r.data));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => {
    const c = { Pending: 0, "Out For Delivery": 0, Delivered: 0, Cancelled: 0 };
    list.forEach(d => { c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [list]);

  // Active drivers logic & telemetry calculation for GPS Route Map
  const driverTelemetry = useMemo(() => {
    const map = {};
    drivers.forEach(d => {
      map[d.id] = {
        driver: d,
        deliveries: [],
        outForDelivery: 0,
        delivered: 0,
        pending: 0,
        lastGps: null,
        routeNames: new Set(),
      };
    });

    list.forEach(item => {
      if (item.driver_id && map[item.driver_id]) {
        const entry = map[item.driver_id];
        entry.deliveries.push(item);
        if (item.route_name) entry.routeNames.add(item.route_name);
        if (item.status === "Delivered") entry.delivered += 1;
        else if (item.status === "Out For Delivery") entry.outForDelivery += 1;
        else entry.pending += 1;

        if (item.gps_lat && item.gps_lng) {
          entry.lastGps = { lat: item.gps_lat, lng: item.gps_lng, delivered_at: item.delivered_at, customer_name: item.customer_name };
        }
      }
    });

    return Object.values(map);
  }, [drivers, list]);

  const activeMapDrivers = useMemo(() => {
    if (mapDriverId === "all") return driverTelemetry;
    return driverTelemetry.filter(dt => dt.driver.id === mapDriverId);
  }, [driverTelemetry, mapDriverId]);

  function toggleAll() {
    if (selected.size === list.length) setSelected(new Set());
    else setSelected(new Set(list.map(d => d.order_id)));
  }
  function toggle(oid) {
    const n = new Set(selected);
    if (n.has(oid)) n.delete(oid); else n.add(oid);
    setSelected(n);
  }

  async function assign() {
    if (!assignDriver) return toast.error("Pick driver");
    if (selected.size === 0) return toast.error("Select at least one delivery");
    try {
      const r = await api.post("/deliveries/assign", {
        driver_id: assignDriver,
        route_id: assignRoute || null,
        order_ids: [...selected],
        delivery_date: assignDate,
      });
      toast.success(`Assigned ${r.data.assigned} deliveries`);
      setOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function changeStatus(d, newStatus) {
    try {
      await api.put(`/deliveries/${d.id}/status`, { status: newStatus, delivery_notes: d.delivery_notes || "" });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="deliveries-page">
      <PageHeader
        eyebrow="Logistics"
        title="Delivery Management"
        description="Assign daily orders to drivers, monitor status, and track live routes with GPS."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-cobalt-200 text-cobalt-700 hover:bg-cobalt-50"
              onClick={() => setMapOpen(true)}
              data-testid="live-gps-map-btn"
            >
              <MapIcon size={14} className="mr-1.5 text-cobalt-600" />
              Live GPS Track & Route Map
            </Button>
            <Button variant="outline" onClick={()=>downloadBlob(`/export/delivery-sheet/${assignDate}.pdf${assignDriver ? `?driver_id=${assignDriver}` : ""}`, `delivery-sheet-${assignDate}.pdf`)} data-testid="delivery-sheet-btn"><FileDown size={14} className="mr-1"/>Delivery Sheet</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-cobalt-500 hover:bg-cobalt-700 text-white" disabled={selected.size === 0} data-testid="assign-btn">
                  <Truck size={14} className="mr-1"/>Assign Selected ({selected.size})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Assign Deliveries</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Driver</Label>
                    <Select value={assignDriver} onValueChange={setAssignDriver}>
                      <SelectTrigger data-testid="assign-driver"><SelectValue placeholder="Select driver"/></SelectTrigger>
                      <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.vehicle_number || "—"})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Route (optional)</Label>
                    <Select value={assignRoute || "none"} onValueChange={(v)=>setAssignRoute(v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="No route"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No route</SelectItem>
                        {routes.map(r => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Delivery Date</Label>
                    <Input type="date" value={assignDate} onChange={e=>setAssignDate(e.target.value)}/>
                  </div>
                  <div className="text-sm text-slate-500"><ClipboardList size={14} className="inline mr-1"/>{selected.size} order(s) selected</div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button onClick={assign} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="assign-save-btn">Assign</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Live GPS Track & Route Map Modal */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0 overflow-hidden" data-testid="live-gps-map-modal">
          <div className="p-4 bg-slate-900 border-b border-slate-800 text-white flex items-center justify-between pr-12">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cobalt-600/30 border border-cobalt-500/50 flex items-center justify-center">
                <Navigation size={20} className="text-cobalt-400 animate-pulse" />
              </div>
              <div>
                <h3 className="font-bold text-base text-white flex items-center gap-2.5">
                  Live GPS Track & Route Map
                  <span className="bg-emerald-500/20 text-emerald-300 text-[11px] uppercase font-semibold px-2.5 py-0.5 rounded-full border border-emerald-500/40 flex items-center gap-1.5">
                    <Radio size={11} className="animate-ping text-emerald-400" /> Active Tracking
                  </span>
                </h3>
                <p className="text-xs text-slate-300">Real-time driver location, active route progress, and delivery telemetry</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={mapDriverId} onValueChange={setMapDriverId}>
                <SelectTrigger className="w-52 bg-slate-800 border-slate-700 text-white text-xs h-9 font-medium shadow-sm">
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 text-white border-slate-700">
                  <SelectItem value="all">All Delivery Drivers ({drivers.length})</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} ({d.vehicle_number || "No Vehicle"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-9 bg-slate-800 border-slate-700 text-slate-200 hover:text-white hover:bg-slate-700 text-xs" onClick={load}>
                <RefreshCw size={13} className="mr-1.5" /> Refresh
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 flex-1 overflow-hidden min-h-[480px]">
            {/* Left Sidebar: Driver Active Routes & Telemetry */}
            <div className="border-r border-slate-200 bg-slate-50 p-4 space-y-3 overflow-y-auto max-h-[520px]">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
                <span>Active Delivery Drivers</span>
                <span className="text-cobalt-600 font-bold">{activeMapDrivers.length} On Duty</span>
              </div>

              {activeMapDrivers.length === 0 ? (
                <div className="text-xs text-slate-500 p-4 text-center">No drivers assigned to current deliveries</div>
              ) : (
                activeMapDrivers.map((dt) => {
                  const isSelected = activeDriverPin === dt.driver.id;
                  const total = dt.deliveries.length;
                  const pct = total > 0 ? Math.round((dt.delivered / total) * 100) : 0;
                  const routeList = Array.from(dt.routeNames).join(", ") || "Standard Dispatch";

                  return (
                    <div
                      key={dt.driver.id}
                      onClick={() => setActiveDriverPin(dt.driver.id)}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        isSelected
                          ? "bg-cobalt-50 border-cobalt-500 shadow-sm"
                          : "bg-white border-slate-200 hover:border-cobalt-300"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold text-xs">
                            {dt.driver.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
                              {dt.driver.name}
                              {dt.lastGps && (
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping inline-block" title="Live GPS Signal Active" />
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500">{dt.driver.vehicle_number || "Vehicle Unassigned"}</div>
                          </div>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 font-semibold text-slate-600">
                          {pct}% Done
                        </span>
                      </div>

                      <div className="mt-2.5 space-y-1">
                        <div className="flex items-center justify-between text-[11px] text-slate-600">
                          <span className="flex items-center gap-1"><MapPin size={11} className="text-slate-400" /> Route:</span>
                          <span className="font-medium text-slate-700 truncate max-w-[130px]">{routeList}</span>
                        </div>
                        {dt.lastGps ? (
                          <div className="text-[10px] font-mono text-emerald-600 flex items-center gap-1">
                            <Navigation size={10} /> GPS: {dt.lastGps.lat.toFixed(4)}, {dt.lastGps.lng.toFixed(4)}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Clock size={10} /> GPS: Pending drop location
                          </div>
                        )}
                      </div>

                      {/* Progress bar */}
                      <div className="mt-2.5 w-full bg-slate-100 h-1.5 rounded-full overflow-hidden flex">
                        <div className="bg-emerald-500 h-full transition-all" style={{ width: `${pct}%` }} />
                      </div>

                      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                        <span className="text-amber-600 font-medium">{dt.outForDelivery} In Transit</span>
                        <span className="text-emerald-600 font-medium">{dt.delivered} Delivered</span>
                        <span className="text-slate-500">{dt.pending} Pending</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right Main Panel: Visual Map View */}
            <div className="md:col-span-2 relative bg-slate-900 flex flex-col items-center justify-center p-4 overflow-hidden min-h-[520px]">
              {/* Simulated Interactive Map Backdrop */}
              <div className="absolute inset-0 opacity-25 bg-[radial-gradient(#0047AB_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
              
              {/* Dynamic Interactive Google Map Canvas Overlay */}
              <div className="w-full h-full relative rounded-lg border border-slate-800 bg-slate-950/70 p-4 flex flex-col justify-between overflow-hidden">
                <div className="flex items-center justify-between z-10">
                  <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700/80 px-3 py-1.5 rounded-lg text-xs font-mono text-slate-200 flex items-center gap-2">
                    <MapPin size={14} className="text-cobalt-400" />
                    <span>Goa Territory Delivery Grid • 15.4989° N, 73.8278° E</span>
                  </div>
                  <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700/80 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-300 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Driver Active
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Out For Delivery
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Stop Location
                  </div>
                </div>

                {/* Simulated Pins on Territory Grid */}
                <div className="relative w-full h-72 my-4 rounded-xl border border-slate-800 bg-gradient-to-b from-slate-900/90 to-slate-950/90 p-4 flex items-center justify-center">
                  <div className="absolute inset-x-8 top-1/2 h-0.5 bg-gradient-to-r from-cobalt-600/20 via-cobalt-500/50 to-emerald-500/30 border-t border-dashed border-cobalt-400/40" />

                  {activeMapDrivers.map((dt, idx) => {
                    const posX = 20 + ((idx * 28) % 65);
                    const posY = 30 + ((idx * 20) % 45);
                    const isFocus = activeDriverPin === dt.driver.id;

                    return (
                      <div
                        key={dt.driver.id}
                        onClick={() => setActiveDriverPin(dt.driver.id)}
                        style={{ left: `${posX}%`, top: `${posY}%` }}
                        className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 ${
                          isFocus ? "scale-125 z-30" : "scale-100 z-20 hover:scale-110"
                        }`}
                      >
                        <div className="relative group">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg border-2 ${
                            dt.lastGps
                              ? "bg-cobalt-600 border-white text-white"
                              : "bg-amber-500 border-white text-white"
                          }`}>
                            <Truck size={16} />
                          </div>
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-slate-900 rounded-full" />
                          
                          {/* Info tooltip */}
                          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-48 bg-slate-900/95 border border-slate-700 text-white rounded-lg p-2.5 shadow-2xl text-[11px] pointer-events-none transition-all">
                            <div className="font-bold text-cobalt-300 flex items-center justify-between">
                              <span>{dt.driver.name}</span>
                              <span className="text-[9px] text-slate-400">{dt.driver.vehicle_number || "No Van"}</span>
                            </div>
                            <div className="text-[10px] text-slate-300 mt-1">
                              Delivered: <span className="text-emerald-400 font-bold">{dt.delivered}</span> / Total: {dt.deliveries.length}
                            </div>
                            {dt.lastGps && (
                              <div className="text-[9px] text-slate-400 mt-1 border-t border-slate-800 pt-1 font-mono">
                                Last Stop: {dt.lastGps.customer_name || "Customer Site"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {activeMapDrivers.length === 0 && (
                    <div className="text-center text-slate-500 text-xs">
                      <MapPin size={24} className="mx-auto mb-2 opacity-40 text-slate-400" />
                      Select a driver from the panel to view active GPS coordinates and route path.
                    </div>
                  )}
                </div>

                {/* Footer status summary */}
                <div className="z-10 bg-slate-900/90 border border-slate-800 p-3 rounded-lg flex items-center justify-between text-xs text-slate-300">
                  <div className="flex items-center gap-2">
                    <Truck size={15} className="text-cobalt-400" />
                    <span>Active Delivery Vehicles: <b>{activeMapDrivers.length}</b></span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>Total Route Drops: <b>{list.length}</b></span>
                    <span className="text-emerald-400 font-semibold">Completed: {counts.Delivered || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="p-3 bg-slate-100 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={() => setMapOpen(false)}>
              Close Map
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card padding="p-4" className="mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Status</Label>
            <Select value={status || "any"} onValueChange={(v)=>setStatus(v === "any" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Any"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Driver</Label>
            <Select value={driverId || "any"} onValueChange={(v)=>setDriverId(v === "any" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Any"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Route</Label>
            <Select value={routeId || "any"} onValueChange={(v)=>setRouteId(v === "any" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Any"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {routes.map(r => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Date</Label>
            <Input type="date" value={date} onChange={e=>setDate(e.target.value)} className="mt-1"/>
          </div>
          <Button onClick={load} variant="outline" data-testid="del-filter-btn">Filter</Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
        <div className="bg-white border border-slate-200 rounded-md p-3"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Pending</div><div className="font-mono font-bold text-lg mt-1">{counts.Pending || 0}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-3"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Out For Delivery</div><div className="font-mono font-bold text-lg mt-1 text-amber-600">{counts["Out For Delivery"] || 0}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-3"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Delivered</div><div className="font-mono font-bold text-lg mt-1 text-emerald-600">{counts.Delivered || 0}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-3"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Cancelled</div><div className="font-mono font-bold text-lg mt-1 text-rose-600">{counts.Cancelled || 0}</div></div>
      </div>

      <Card padding="p-0">
        {list.length === 0 ? <div className="p-8"><Empty title="No deliveries"/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="deliveries-table">
              <thead><tr>
                <th><Checkbox checked={list.length > 0 && selected.size === list.length} onCheckedChange={toggleAll} aria-label="Select all"/></th>
                <th>Date</th><th>Invoice</th><th>Customer</th><th>Driver</th><th>Route</th><th>Status</th><th className="num">Change</th>
              </tr></thead>
              <tbody>
                {list.map(d => (
                  <tr key={d.id}>
                    <td><Checkbox checked={selected.has(d.order_id)} onCheckedChange={()=>toggle(d.order_id)} aria-label="Select"/></td>
                    <td className="font-mono text-xs">{fmtDate(d.delivery_date)}</td>
                    <td className="font-mono text-xs text-cobalt-600">{d.invoice_no}</td>
                    <td className="font-medium">
                      {d.customer_name}
                      <div className="text-[11px] text-slate-500 flex items-center gap-1">
                        <MapPin size={10} className="text-slate-400" />
                        {d.customer_address}
                        {d.gps_lat && d.gps_lng && (
                          <span className="text-[10px] font-mono text-emerald-600 font-semibold ml-1">
                            ({d.gps_lat.toFixed(3)}, {d.gps_lng.toFixed(3)})
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{d.driver_name || <span className="text-slate-400">—</span>}</td>
                    <td>{d.route_name || <span className="text-slate-400">—</span>}</td>
                    <td><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR[d.status] || "bg-slate-100"}`}>{d.status}</span></td>
                    <td className="num">
                      <Select value={d.status} onValueChange={(v)=>changeStatus(d, v)}>
                        <SelectTrigger className="w-44 ml-auto"><SelectValue/></SelectTrigger>
                        <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

